import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Address, BusinessSummary } from 'lib/types/models';
import { Dispatch } from 'redux';
import {
  Entity,
  showEntityRecord as showEntityRecordRedux,
} from 'services/redux/entityRecordSlice';

import { ResellDocument } from '../../pages/Finance/Components/Funds/FactoringFundReSell/components/NewMultiResell/types';
import { showAlert as showAlertRedux } from '../../services/redux/alertSlice';
import { User } from '../../services/redux/authSlice';

export const formatMoney = (value: number) => {
  const formatter = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0, // No decimals
    maximumFractionDigits: 0, // No decimals
  });
  if (!value) return `$0`;
  if (formatter.format(value) === 'NaN') return `${value}`;
  return `$${formatter.format(value)}`;
};

export const formatMoneyUF = (value: number) => {
  const formatter = new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
  if (!value) return `UF 0`;
  if (formatter.format(value) === 'NaN') return `${value}`;
  return `UF ${formatter.format(value)}`;
};

export const formatDate = (date: Date, format?: string) => {
  const dateObject = new Date(date);
  if (isNaN(dateObject.getTime())) return 'Fecha inválida';
  dateObject.setHours(dateObject.getHours());
  const year = dateObject.getUTCFullYear();
  const month = dateObject.getUTCMonth() + 1; // Add 1 because months are zero-based
  const day = dateObject.getUTCDate();
  const hours = dateObject.getUTCHours();
  const minutes = dateObject.getUTCMinutes();

  switch (format) {
    case 'large':
      return `${day.toString().padStart(2, '0')}-${month.toString().padStart(2, '0')}-${year} ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    case 'yyyy-mm-dd':
      return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    case 'dd-mm-yyyy':
      return `${day.toString().padStart(2, '0')}-${month.toString().padStart(2, '0')}-${year}`;
    default:
      return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
  }
};

export const isValidChileanPhone = (phone: string) => {
  return /^56\d{9}$/.test(phone);
};

export const visibilityOptions = [
  { id: 'full_visibility', name: 'Visiblidad total' },
  { id: 'partial_visibility', name: 'Visiblidad parcial' },
  { id: 'no_visibility', name: 'Sin visiblidad' },
];

export const visibilityDurationOptions = [
  { id: 'always_visible', name: 'Sin límite' },
  { id: 'temporary_visible', name: 'Temporal' },
];

export function validateParams(
  params: any,
  requiredFields: string[],
  setFormErrorMessage: any,
): boolean {
  for (const field of requiredFields) {
    if (
      params[field] === null ||
      params[field] === undefined ||
      params[field] === ''
    ) {
      setFormErrorMessage(`Missing required field: ${field}`);
      return false;
    }
  }
  return true;
}
const RUT_REGEX = /^\d{1,8}-[kK\d]$/;

export const rutChanger = (value: string, setter: (value: string) => void) => {
  const allowedChars = '0123456789kK-';
  const newRut = value.split('').filter((char) => allowedChars.includes(char));
  setter(newRut.join('').toUpperCase());
};

export function validaRut(rut: string): boolean {
  // Validar el formato con regex
  if (!RUT_REGEX.test(rut)) return false;

  // Separar número y dígito verificador
  const [numero, dv] = rut.split('-');

  let factor = 2;
  let suma = 0;

  // Recorrer los dígitos del RUT de derecha a izquierda
  for (let i = numero.length - 1; i >= 0; i--) {
    suma += parseInt(numero[i], 10) * factor;
    factor = factor === 7 ? 2 : factor + 1;
  }

  let mod: string | number = 11 - (suma % 11);
  if (mod === 11) mod = 0;
  if (mod === 10) mod = 'K';

  return mod.toString() === dv.toUpperCase();
}

export function validatePermissions(user: any, requiredPermission?: string) {
  const admin = user.roles.find((x: any) => x.id === 4 || x.id === 1);
  if (admin) return true;

  if (!requiredPermission) return false;
  const hasPermission = user?.roles.some((role: any) =>
    role.permissions.some(
      (permission: any) => permission.name === requiredPermission,
    ),
  );
  return hasPermission;
}

export function hasAnyPermissions(
  user: User | undefined,
  requiredPermissions: string[],
) {
  if (!user?.roles) return false;

  const admin = user.roles.find((x: any) => x.id === 4 || x.id === 1);
  if (admin) return true;

  const hasPermission = user.roles.some((role: any) =>
    role.permissions.some((permission: any) =>
      requiredPermissions.includes(permission.name),
    ),
  );
  return hasPermission;
}

export function validateCompanyTypes(user: any, requiredType?: string) {
  // const admin = user.roles.find((x: any) => x.id === 4 ||  x.id === 1)
  // if (admin) return true

  if (!requiredType) return false;

  const hasPermission = user?.companyTypes.some(
    (type: any) => type.name === requiredType,
  );

  return hasPermission;
}

export function hasAnyCompanyTypes(
  user: User | undefined,
  requiredTypes: string[],
) {
  if (!user?.companyTypes) return false;

  const hasPermission = user.companyTypes.some((type: any) =>
    requiredTypes.includes(type.name),
  );

  return hasPermission;
}

export function validateProducts(
  user: User | undefined,
  requiredProducts: string[],
) {
  if (!user?.products) return false;
  return requiredProducts.some((product) =>
    user.products?.some((x) => x.typeId === product),
  );
}

export function isAdmin(user: any) {
  const admin = user.roles.find((x: any) => x.id === 4 || x.id === 1);
  if (admin) return true;
  return false;
}

export function isSuperAdmin(user: any) {
  const superAdmin = user.roles.find((x: any) => x.id === 4);
  if (superAdmin) return true;
  return false;
}

export function calculateInterest(
  monthlyRate: number,
  retention: number,
  amountTotal: number,
  paymentDate: Date,
) {
  const dailyRate = monthlyRate / 100 / 30;
  const retentionDecimal = retention / 100;
  const effectiveAmount = amountTotal * (1 - retentionDecimal);

  const today = new Date();
  const timeDiff = paymentDate.getTime() - today.getTime();
  const daysUntilPayment = Math.ceil(timeDiff / (1000 * 3600 * 24));

  const totalInterest = effectiveAmount * dailyRate * daysUntilPayment;
  return totalInterest;
}

export const renderSelectedNames = (selectedIds: any, array: any) => {
  return selectedIds
    .map((id: any) => {
      const foundItem = array.find((item: any) => item.id === id);
      return foundItem ? foundItem.name : '';
    })
    .filter((name: any) => name) // Filter out any undefined or empty names
    .join(', ');
};

export const handleSelectChange = (e: any, setMethod: any, oldValue: any) => {
  const value = e.target.value;
  // Check if the target has the 'options' property, which is true for multiple selects
  const isMultiple = e.target.options;

  if (isMultiple) {
    // If it's a multiple select, create an array of the selected values
    const selectedValues = Array.from(e.target.options)
      .filter((option: any) => option.selected)
      .map((option: any) => option.value);
    setMethod({
      ...oldValue,
      [e.target.name]: selectedValues,
    });
  } else {
    // If it's a single select, set the value as it is
    setMethod({
      ...oldValue,
      [e.target.name]: value,
    });
  }
};

export const generatScorePDF = (assgination: any, rows: any, user: any) => {
  const doc = new jsPDF();
  let startY = 20; // Starting Y position for the first table

  const { name, businessName, businessIdentifier, rating, score } = assgination;
  doc.setFontSize(14);
  // summary table
  const leftStart = 14;
  const left2column = 120;
  doc.text('Ficha Clasificación Riesgo', leftStart, startY); // Adjust the Y position for the title
  //
  startY = startY + 10;
  const startVariables = leftStart + 25;
  const startVariables2column = left2column + 30;

  doc.setFontSize(10);
  doc.text('Razón social: ', leftStart, startY);
  doc.text(businessName, startVariables, startY);

  doc.text('Rut: ', leftStart, startY + 5);
  doc.text(businessIdentifier, startVariables, startY + 5);

  doc.text('Clasficación: ', leftStart, startY + 15);
  doc.text(rating, startVariables, startY + 15);

  doc.text('Score: ', leftStart, startY + 20);
  doc.text(score, startVariables, startY + 20);

  doc.text('Plantilla utilizada: ', left2column, startY + 15);
  doc.text(name, startVariables2column, startY + 15);

  doc.text('Fecha evaluación: ', left2column, startY + 20);
  doc.text(
    new Date().toISOString().split('T')[0],
    startVariables2column,
    startY + 20,
  );

  doc.text('Ejecutivo: ', leftStart, startY + 30);
  doc.text(user.name, startVariables, startY + 30);

  // if (user.companyId){
  //   doc.addImage(`https://pruff-public.s3.amazonaws.com/company-logos/${user.companyId}.png`, 'JPEG', 100, startY, 60, 40);
  // }

  startY = startY + 30;

  const columns = [
    { title: 'Concepto', dataKey: 'name' },
    { title: 'Peso', dataKey: 'weight' },
    { title: 'Evaluación', dataKey: 'description' },
    { title: 'Input Score', dataKey: 'inputScore' },
    { title: 'Observaciones', dataKey: 'observation' },
  ];

  autoTable(doc, {
    columns: columns,
    startY: startY + 5,
    body: rows,
    theme: 'grid',
    headStyles: {
      fillColor: '#efefef', // or '#2980b9' in hex format
      textColor: '#1b1b1b',
      fontStyle: 'bold',
      halign: 'left', // horizontal alignment
      valign: 'middle', // vertical alignment
      lineColor: [44, 62, 80],
      fontSize: 12,
    },
    bodyStyles: {
      textColor: [50, 50, 50],
      fontSize: 8,
    },
    styles: {
      font: 'helvetica', // choose the font
      lineColor: [44, 62, 80],
      lineWidth: 0.1,
    },
    // For each column, you can define individual styles as well, for example:
    columnStyles: {
      0: { fontStyle: 'bold' }, // Specific styles for the first column (index 0)
    },
    // Add other options as needed
  });

  // Save the PDF
  doc.save(`${businessName}/Score.pdf`);
};

export const flattenArrayObject = (array: any[]): any => {
  // receives an array of object with sub object inside and returns an array of objects with all the keys
  return array.map((item: any) => {
    const newItem: any = {};
    for (const key of Object.keys(item)) {
      if (typeof item[key] === 'object' && item[key] !== null) {
        for (const subKey of Object.keys(item[key])) {
          if (
            typeof item[key][subKey] === 'object' &&
            item[key][subKey] !== null
          ) {
            for (const subSubKey of Object.keys(item[key][subKey])) {
              newItem[key + '_' + subKey + '_' + subSubKey] =
                item[key][subKey][subSubKey];
            }
          } else {
            newItem[key + '_' + subKey] = item[key][subKey];
          }
        }
      } else if ('type' === key) {
        newItem['element_' + key] = item[key];
      } else {
        newItem[key] = item[key];
      }
    }
    return newItem;
  });
};

export const calculateRequestDetails = (
  selectedRequest: any,
  expensesHasIVA: boolean,
  factoringOperationsSystemFlag?: string,
) => {
  let exchangeDays;
  if (
    selectedRequest.exchangeDays !== undefined &&
    selectedRequest.exchangeDays !== null
  ) {
    exchangeDays = selectedRequest.exchangeDays
      ? Number(selectedRequest.exchangeDays)
      : 0;
  } else if (
    selectedRequest.quoteActive?.exchangeDays !== undefined &&
    selectedRequest.quoteActive?.exchangeDays !== null
  ) {
    exchangeDays = selectedRequest.quoteActive.exchangeDays
      ? Number(selectedRequest.quoteActive.exchangeDays)
      : 0;
  } else {
    exchangeDays = 0;
  }
  const formData = {
    advance: selectedRequest.advance,
    rate: selectedRequest.rate,
    operationCostComissionFixed:
      selectedRequest.operationCostComissionFixed ||
      selectedRequest.operationCostFixed ||
      0,
    operationCostComissionVariable:
      selectedRequest.operationCostComissionVariable ||
      selectedRequest.operationCostVariable ||
      0,
    expenses: selectedRequest.expenses || [],
    amountToApply: selectedRequest.amountToApply,
    guaranteeAmount: selectedRequest.guaranteeAmount || 0,
    guaranteeObservation: selectedRequest.guaranteeObservation,
    arrearType: selectedRequest.arrearType || 'lineal',
    arrearTableId: selectedRequest.arrearTableId || null,
    exchangeDays,
  };

  let operationAmount = 0;
  let operationAdvanced = 0;
  let operationCostRate = 0;
  const documentsWithDetails = [];
  for (const document of selectedRequest.documents) {
    const documentAmount = document.amount * (document.active ? 1 : 0);
    const { daysToCount } = document;
    const amountFinanced = Math.round(
      documentAmount * (formData.advance / 100),
    );
    const amountRetention = Math.round(
      documentAmount * (1 - formData.advance / 100),
    );
    const amountDocument = documentAmount - amountRetention;
    const costRate = calculateRateCost(
      daysToCount,
      amountFinanced,
      formData.rate,
      selectedRequest?.firstQuote?.systemFlag || factoringOperationsSystemFlag,
      document.paymentDate,
      formData.exchangeDays,
    );
    operationAmount += documentAmount;
    operationAdvanced += amountFinanced;
    operationCostRate += costRate;
    const amountToDeposit = amountFinanced - costRate;

    documentsWithDetails.push({
      ...document,
      amountDocument,
      amountFinanced,
      amountRetention,
      amountToDeposit,
      costRate,
    });
  }
  const operationIva = Math.round(
    0.19 *
      (formData.operationCostComissionFixed +
        Math.round(formData.operationCostComissionVariable)),
  );

  const expensesIva = formData.expenses.reduce(
    (
      acc: number,
      expense: { name: string; amount: number; hasIva: boolean },
    ) => {
      return acc + (expense.hasIva ? 0.19 * expense.amount : 0);
    },
    0,
  );
  const expensesCost = formData.expenses.reduce(
    (
      acc: number,
      expense: { name: string; amount: number; hasIva: boolean },
    ) => {
      return acc + expense.amount;
    },
    0,
  );
  const operationToDepost =
    (operationAdvanced || 0) -
    (formData.guaranteeAmount || 0) -
    (formData.operationCostComissionVariable || 0) -
    (formData.operationCostComissionFixed || 0) -
    (expensesCost || 0) -
    (operationCostRate || 0) -
    (formData.amountToApply || 0) -
    (operationIva || 0) -
    (expensesIva || 0);
  return {
    advance: formData.advance,
    rate: formData.rate,
    operationAmount,
    operationAdvanced,
    operationCostRate,
    operationAmountToApply: formData.amountToApply,
    operationToDepost,
    operationIva,
    expensesIva,
    operationCostComissionFixed: formData.operationCostComissionFixed,
    operationCostComissionVariable: formData.operationCostComissionVariable,
    expensesCost: expensesCost,
    guaranteeAmount: formData.guaranteeAmount,
    documentsWithDetails,
    guaranteeObservation: formData.guaranteeObservation,
    arrearType: formData.arrearType,
    arrearTableId: formData.arrearTableId,
    expensesDetails: formData.expenses,
  };
};

export const calculateRateCost = (
  daysToCount: number,
  amountFinanced: number,
  rate: number,
  systemFlag?: string,
  paymentDate?: Date,
  exchangeDays?: number,
) => {
  switch (systemFlag) {
    case 'PROSYSTEM':
      return calculateProsystemRateCost(
        daysToCount,
        amountFinanced,
        rate,
        paymentDate,
      );
    case 'DBTHOR':
      return calculateDbthorRateCost(
        daysToCount,
        amountFinanced,
        rate,
        paymentDate,
      );
    case 'PRUFF':
    default:
      return calculatePruffRateCost(
        daysToCount,
        amountFinanced,
        rate,
        exchangeDays,
      );
  }
};

const calculateProsystemRateCost = (
  daysToCount: number,
  amountFinanced: number,
  rate: number,
  paymentDate?: Date,
) => {
  // PROSYSTEM uses different calculation for cost rate:
  // BaseLine, add one day to the daysToCount
  // Then, if the paymentDate is friday, add 2 days, if its saturday, add 1 day
  let daysToCountProsystem = daysToCount + 1;

  if (paymentDate) {
    const paymentDateDay = new Date(paymentDate);
    const dayOfWeek = paymentDateDay.getDay() + 1;

    switch (dayOfWeek) {
      case 5: // Friday
        daysToCountProsystem += 2;
        break;
      case 6: // Saturday
        daysToCountProsystem += 1;
        break;
    }
  }

  return daysToCountProsystem
    ? Math.round((daysToCountProsystem / 30) * (rate / 100) * amountFinanced)
    : 0;
};

const calculateDbthorRateCost = (
  daysToCount: number,
  amountFinanced: number,
  rate: number,
  paymentDate?: Date | string,
) => {
  let daysToCountDbthor = daysToCount + 3; // Default

  if (paymentDate) {
    const paymentDateDay = new Date(paymentDate);
    const dayOfWeek = paymentDateDay.getDay() + 1;
    switch (dayOfWeek) {
      case 4: // Thursday
        daysToCountDbthor += 2;
        break;
      case 5: // Friday
        daysToCountDbthor += 2;
        break;
      case 6: // Saturday
        daysToCountDbthor += 2;
        break;
      case 0: // Sunday
        daysToCountDbthor += 2;
        break;
    }
  }

  return daysToCountDbthor
    ? Math.round((daysToCountDbthor / 30) * (rate / 100) * amountFinanced)
    : 0;
};

const calculatePruffRateCost = (
  daysToCount: number,
  amountFinanced: number,
  rate: number,
  exchangeDays?: number,
) => {
  return daysToCount
    ? Math.round(
        ((daysToCount + (exchangeDays || 0)) / 30) *
          (rate / 100) *
          amountFinanced,
      )
    : 0;
};

export const formatDateBack = (date: string | Date) => {
  if (!date) return '';
  if (typeof date !== 'string') date = date.toString();
  const day = date.slice(0, 2);
  const month = date.slice(2, 4);
  const year = date.slice(4, 8);
  return `${year}-${month}-${day}`;
};

export const formatMoneyBack = (value: string): number => {
  return parseInt(value.split('$').join('').split('.').join('')) || 0;
};

export const isValidRate = (value: string) => {
  if (value === '') {
    return true;
  }
  if (isNaN(Number(value))) {
    return false;
  }
  const valueNumber = parseFloat(value);
  if (valueNumber >= 0 && valueNumber <= 100) {
    return true;
  } else {
    return false;
  }
};

export const copyToClipboard = async (str: string) => {
  try {
    await navigator.clipboard.writeText(str);
  } catch (err) {
    console.error('No se pudo copiar el texto: ', err);
  }
};

export const loggerDev = (...args: any[]) => {
  if (process.env.NODE_ENV !== 'production' && args.length > 0) {
    console.log(args.join(' '));
  }
};

export const showAlert = (
  dispatch: Dispatch,
  severity: 'success' | 'warning' | 'error',
  message: string,
) => {
  dispatch(showAlertRedux({ message, severity }));
};

export const getShortString = (string: string, maxLength: number = 25) => {
  return string.length > maxLength
    ? string.substring(0, maxLength) + '...'
    : string;
};

export const getChileUtcOffset = () => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Santiago',
    timeZoneName: 'shortOffset',
  });
  const parts = formatter.formatToParts(new Date());
  const offsetPart = parts.find((p) => p.type === 'timeZoneName');
  if (!offsetPart) {
    throw new Error('Cannot determine Chile UTC offset');
  }
  const match = offsetPart.value.match(/GMT([+-]\d+)/);
  if (!match) {
    return -4;
  }
  return parseInt(match[1], 10);
};

export const formatAmountByCurrency = (value: string, currency: string) => {
  if (!value) return '';
  const num = Number(value.replace(/[^0-9]/g, ''));
  if (currency === 'UF') {
    return `UF ${num.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (currency === 'USD') {
    return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  return `$${num.toLocaleString('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

export function applyDefaults<T extends Record<string, unknown>>(
  current: T,
  defaults: T,
): T {
  const result = { ...defaults };

  for (const key in current) {
    if (current[key] !== null) {
      result[key] = current[key];
    }
  }

  return result;
}
export const generatePaginationQuery = (params: {
  page: number;
  pageSize: number;
  businessIdentifier: string;
}) => {
  const query = new URLSearchParams();
  const { page, pageSize, businessIdentifier } = params;
  query.append('page', page.toString());
  query.append('pageSize', pageSize.toString());
  query.append('businessIdentifier', businessIdentifier);
  return query.toString();
};

const hexToRgb = (hex: string) => {
  hex = hex.replace(/^#/, '');
  if (hex.length !== 6) {
    throw new Error('Formato inválido. Usa #RRGGBB');
  }
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return { r, g, b };
};

const luminanceWCAG = (r: number, g: number, b: number) => {
  const a = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
};

export const getBestTextColorFromHex = (hex: string) => {
  const { r, g, b } = hexToRgb(hex);
  const Lbg = luminanceWCAG(r, g, b);
  const whiteContrast = 1.05 / (Lbg + 0.05);
  const blackContrast = (Lbg + 0.05) / 0.05;
  return whiteContrast > blackContrast ? 'white' : 'black';
};

export const formatAddress = (
  address: Address | BusinessSummary | null | undefined,
) => {
  if (!address) return 'Sin dirección disponible';
  const parts = [
    address.address,
    address.commune,
    address.province,
    address.region,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : 'Sin dirección disponible';
};

export interface ResellSummary {
  avgRate: number;
  rateCost: number;
  operationCostFixed: number;
  operationCostFixedWithIVA: number;
  totalAmount: number;
  amountToReceive: number;
}

export const calculateResellSummary = (
  resells: ResellDocument[],
  amountToApply: number,
): ResellSummary => {
  if (!resells || resells.length === 0) {
    return {
      avgRate: 0,
      rateCost: 0,
      operationCostFixed: 0,
      operationCostFixedWithIVA: 0,
      totalAmount: 0,
      amountToReceive: 0,
    };
  }
  const totalAmount = resells.reduce(
    (acc: number, resell: any) =>
      acc + Number(resell.amount * (resell.advance / 100) || 0),
    0,
  );

  const avgRate = Number(
    (
      resells.reduce(
        (acc: number, resell: any) =>
          acc +
          Number(resell.rate || 0) *
            Number(resell.amount * (resell.advance / 100) || 0),
        0,
      ) / totalAmount
    ).toFixed(2),
  );

  const rateCost = resells.reduce(
    (acc: number, resell: any) => acc + Number(resell.rateCost || 0),
    0,
  );

  const operationCostFixed = resells.reduce(
    (acc: number, resell: any) => acc + Number(resell.operationCostFixed || 0),
    0,
  );

  const operationCostFixedWithIVA = operationCostFixed * 1.19;

  const amountToReceive =
    totalAmount - rateCost - operationCostFixedWithIVA - amountToApply;

  return {
    avgRate,
    rateCost,
    operationCostFixed,
    operationCostFixedWithIVA,
    totalAmount,
    amountToReceive,
  };
};

export const calculateNomineeSummary = (nominee: any): ResellSummary => {
  const resells = nominee.resells || [];
  const amountToApply = nominee.amountToApply || 0;
  return calculateResellSummary(resells, amountToApply);
};

export const showEntityRecord = (dispatch: Dispatch, entity: Entity) => {
  dispatch(showEntityRecordRedux(entity));
};

export function parseRutWithDots(
  rut: string | null | undefined,
): string | null {
  if (!rut) return null;

  const cleaned = rut.replace(/\./g, '').replace(/\s/g, '');
  const [numberPartRaw, dvRaw] = cleaned.split('-');

  if (!dvRaw) return cleaned;

  const numberPart = (numberPartRaw || '').replace(/\D/g, '');
  const dv = dvRaw.toUpperCase();

  if (!numberPart) return cleaned;

  const reversed = numberPart.split('').reverse();
  const chunks: string[] = [];
  for (let i = 0; i < reversed.length; i += 3) {
    chunks.push(reversed.slice(i, i + 3).join(''));
  }
  const withDots = chunks
    .map((c) => c.split('').reverse().join(''))
    .reverse()
    .join('.');

  return `${withDots}-${dv}`;
}

export const formatPercentage = (value: string | null | undefined) => {
  if (!value) return null;
  return `${value}%`;
};

export function isEnumKey<T extends Record<string, string | number | symbol>>(
  enumObj: T,
  value: string | number | symbol,
): value is keyof T {
  return Object.prototype.hasOwnProperty.call(enumObj, value);
}

export function isEnumValue<T extends Record<string, string | number | symbol>>(
  enumObj: T,
  value: string | number | symbol,
): value is T[keyof T] {
  return Object.values(enumObj).includes(value);
}

export const buildValidation = (
  isEmpty: boolean,
  isInvalid: boolean,
  emptyMessage: string,
  invalidMessage: string,
) => {
  const hasError = isEmpty || isInvalid;
  const message = isEmpty ? emptyMessage : isInvalid ? invalidMessage : '';

  return { hasError, message };
};

export const isValidEmail = (email: string | null | undefined) => {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export function getFilenameDateString() {
  // Expected output: 2025-12-12 at 11.55.32 AM
  const now = new Date();
  const dateString = now.toLocaleDateString('en-CA'); // YYYY-MM-DD
  const timeString = now
    .toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    })
    .replace(/:/g, '.'); // HH.MM.SS AM/PM
  return `${dateString} at ${timeString}`;
}

export const formatCurrency = (
  amount: number | null | undefined,
  currency: 'CLP' | 'USD' | 'UF' | null | undefined = 'CLP',
) => {
  if (!amount) return 'Sin monto disponible';
  switch (currency) {
    case 'CLP':
      return (
        amount.toLocaleString('es-CL', {
          style: 'currency',
          currency,
        }) + ' CLP'
      );
    case 'USD':
      return (
        amount.toLocaleString('en-US', {
          style: 'currency',
          currency,
        }) + ' USD'
      );
    case 'UF':
      return (
        amount.toLocaleString('es-CL', {
          minimumFractionDigits: 3,
          maximumFractionDigits: 3,
        }) + ' UF'
      );
    default:
      return 'Sin monto disponible';
  }
};

export const formatDateString = (
  date: string | null | undefined,
  format?: string,
) => {
  if (!date) return 'Fecha inválida';
  const dateObject = new Date(date);
  if (isNaN(dateObject.getTime())) return 'Fecha inválida';
  const year = dateObject.getUTCFullYear();
  const month = dateObject.getUTCMonth() + 1;
  const day = dateObject.getUTCDate();

  switch (format) {
    case 'yyyy-mm-dd':
      return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    case 'dd-mm-yyyy':
      return `${day.toString().padStart(2, '0')}-${month.toString().padStart(2, '0')}-${year}`;
    case 'dd/mm/yyyy':
      return `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year}`;
    default:
      return `${day.toString().padStart(2, '0')}-${month.toString().padStart(2, '0')}-${year}`;
  }
};
